import React, {useEffect, useState} from 'react';
import {toast} from 'react-toastify';
import api from '../services/api';
import type {DraftOrder, DraftOrderDetail, DraftOrderItemInput} from '../types/myaccount';

interface DraftOrderSelectorModalProps {
    isOpen: boolean;
    onClose: () => void;
    itemPayload: DraftOrderItemInput | null;
    itemName?: string;
    title?: string;
    canCreateDraft?: boolean;
    canAddToExisting?: boolean;
    onSuccess?: (draft: DraftOrderDetail) => void;
}

const DraftOrderSelectorModal: React.FC<DraftOrderSelectorModalProps> = ({
    isOpen,
    onClose,
    itemPayload,
    itemName,
    title = 'Add to Draft Order',
    canCreateDraft = true,
    canAddToExisting = true,
    onSuccess,
}) => {
    const [drafts, setDrafts] = useState<DraftOrder[]>([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [newDraftName, setNewDraftName] = useState('');

    useEffect(() => {
        if (!isOpen) {
            setErrorMessage('');
            setNewDraftName('');
            return;
        }

        let cancelled = false;
        setLoading(canAddToExisting);
        setErrorMessage('');

        if (!canAddToExisting) {
            setDrafts([]);
            setLoading(false);
            return () => {
                cancelled = true;
            };
        }

        api.invalidateCache('weka/v1/draft-orders');
        api.getDraftOrders()
            .then((response) => {
                if (!cancelled) {
                    setDrafts(Array.isArray(response) ? response : []);
                }
            })
            .catch((error) => {
                if (!cancelled) {
                    const message = error instanceof Error ? error.message : 'Failed to load draft orders.';
                    setErrorMessage(message);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [isOpen, canAddToExisting]);

    if (!isOpen) {
        return null;
    }

    const closeButtonClassName = `selector-modal__close${submitting ? ' is-disabled' : ''}`;
    const addToDraftButtonClassName = `button weka-secondary-btn selector-modal__button selector-modal__button--secondary${submitting || !itemPayload ? ' is-disabled' : ''}`;
    const createDraftButtonClassName = `button weka-primary-btn selector-modal__button selector-modal__button--primary${submitting || !itemPayload ? ' is-disabled' : ''}`;

    const handleAddToExisting = async (draftId: number) => {
        if (!canAddToExisting || !itemPayload || submitting) {
            return;
        }

        setSubmitting(true);
        try {
            const response = await api.addDraftOrderItem(draftId, itemPayload);
            toast.success(response.message);
            onSuccess?.(response.draft);
            onClose();
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to add item to draft order.';
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleCreateDraft = async () => {
        if (!canCreateDraft || !itemPayload || submitting) {
            return;
        }

        setSubmitting(true);
        try {
            const response = await api.createDraftOrder({
                draft_name: newDraftName.trim(),
                items: [itemPayload],
                mode: 'manual',
            });
            setDrafts((currentDrafts) => [response.draft, ...currentDrafts.filter((draft) => draft.id !== response.draft.id)]);
            toast.success(response.message);
            onSuccess?.(response.draft);
            onClose();
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to create draft order.';
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="selector-modal-overlay" onClick={() => { if (!submitting) onClose(); }}>
            <div
                className="selector-modal"
                onClick={(event) => event.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="draft-order-selector-title"
            >
                <div className="selector-modal__header">
                    <div>
                        <div id="draft-order-selector-title" className="selector-modal__title">{title}</div>
                        <p className="selector-modal__subtitle">
                            {itemName && itemName.trim() !== ''
                                ? `Choose where to save ${itemName}.`
                                : 'Choose an existing draft or create a new one.'}
                        </p>
                    </div>
                    <button
                        type="button"
                        aria-label="Close"
                        disabled={submitting}
                        onClick={onClose}
                        className={closeButtonClassName}
                    >
                        &times;
                    </button>
                </div>

                <div className="selector-modal__body">
                    {errorMessage !== '' && (
                        <div className="selector-modal__error" role="alert">
                            {errorMessage}
                        </div>
                    )}

                    {canAddToExisting && (
                        <section className="selector-modal__section">
                            <div className="selector-modal__section-header">
                                <div className="selector-modal__section-title">Existing Drafts</div>
                                {loading && <span className="selector-modal__loading-text">Loading...</span>}
                            </div>

                            {!loading && drafts.length === 0 && (
                                <div className="selector-modal__empty-state">
                                    No draft orders found yet.
                                </div>
                            )}

                            <div className="selector-modal__list">
                                {drafts.map((draft) => (
                                    <div
                                        key={draft.id}
                                        className="selector-modal__list-item"
                                    >
                                        <div className="selector-modal__list-meta">
                                            <p className="selector-modal__list-title">{draft.name}</p>
                                            <p className="selector-modal__list-summary">
                                                {draft.items_count} items
                                                {' | '}
                                                ${draft.total}
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => { void handleAddToExisting(draft.id); }}
                                            disabled={submitting || !itemPayload}
                                            className={addToDraftButtonClassName}
                                        >
                                            {submitting ? 'Saving...' : 'Add to This Draft'}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {canCreateDraft && (
                        <section className="selector-modal__section selector-modal__section--divided">
                            <div>
                                <div className="selector-modal__section-title">Create New Draft</div>
                                <p className="selector-modal__section-hint">You can leave the name blank and rename it later.</p>
                            </div>
                            <div className="selector-modal__input-row">
                                <input
                                    type="text"
                                    placeholder="Draft name"
                                    value={newDraftName}
                                    onChange={(event) => setNewDraftName(event.currentTarget.value)}
                                    disabled={submitting}
                                    className="selector-modal__input"
                                />
                                <button
                                    type="button"
                                    onClick={() => { void handleCreateDraft(); }}
                                    disabled={submitting || !itemPayload}
                                    className={createDraftButtonClassName}
                                >
                                    {submitting ? 'Creating...' : 'Create and Add'}
                                </button>
                            </div>
                        </section>
                    )}

                    {!canAddToExisting && !canCreateDraft && (
                        <div className="selector-modal__empty-state">
                            Saving products to draft orders is disabled for this page.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DraftOrderSelectorModal;
